import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import GoogleIcon from '@mui/icons-material/Google';
import { getFieldError } from '../../schemas/forms';
import { Card, SignInContainer } from './LoginPage.styles';
import { useLoginForm } from './useLoginForm';
import { extractErrorCode } from '../../lib/errorCodes';

export default function LoginPage() {
	const {
		formData,
		validationErrors,
		showPassword,
		loginMutation,
		handleTogglePasswordVisibility,
		handleGoogleSignIn,
		handleSubmit,
		handleInputChange,
	} = useLoginForm();

	return (
		<>
			<CssBaseline enableColorScheme />
			<SignInContainer direction="column" justifyContent="center">
				<Card variant="outlined">
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
						<Typography
							component="h1"
							variant="h4"
							sx={{ fontSize: 'clamp(1.5rem, 8vw, 2rem)', color: 'primary.main' }}
						>
							Hệ thống quản lý thông tin tuyển sinh
						</Typography>
					</Box>

					<Typography
						component="h2"
						variant="h5"
						sx={{ width: '100%', textAlign: 'center', mb: 2 }}
					>
						Đăng nhập
					</Typography>

					{loginMutation.error && (() => {
						const code = extractErrorCode(loginMutation.error);
						const isInvalidCreds = code === '10001' || code === '10002';
						return (
							<Alert severity="error" sx={{ mb: 2 }}>
								{isInvalidCreds
									? `Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin. Mã lỗi E-${code}`
									: `Đăng nhập thất bại. Mã lỗi E-${code}`}
							</Alert>
						);
					})()}

					<Box
						component="form"
						onSubmit={handleSubmit}
						noValidate
						sx={{
							display: 'flex',
							flexDirection: 'column',
							width: '100%',
							gap: 2,
						}}
					>
						<FormControl>
							<FormLabel htmlFor="email">Tên đăng nhập/Email</FormLabel>
							<TextField
								error={!!getFieldError(validationErrors, 'email')}
								helperText={getFieldError(validationErrors, 'email')}
								id="email"
								type="text"
								name="email"
								autoComplete="username"
								autoFocus
								required
								fullWidth
								variant="outlined"
								value={formData.email}
								onChange={handleInputChange('email')}
								color={getFieldError(validationErrors, 'email') ? 'error' : 'primary'}
							/>
						</FormControl>
						<FormControl>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
								<FormLabel htmlFor="password">Mật khẩu</FormLabel>
								<Link component={RouterLink} to="/forgot-password" variant="body2">
									Quên mật khẩu?
								</Link>
							</Box>
							<TextField
								error={!!getFieldError(validationErrors, 'password')}
								helperText={getFieldError(validationErrors, 'password')}
								name="password"
								placeholder="••••••"
								type={showPassword ? 'text' : 'password'}
								id="password"
								autoComplete="current-password"
								required
								fullWidth
								variant="outlined"
								value={formData.password}
								onChange={handleInputChange('password')}
								color={getFieldError(validationErrors, 'password') ? 'error' : 'primary'}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={handleTogglePasswordVisibility}
												edge="end"
												size="small"
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									)
								}}
							/>
						</FormControl>
						<FormControlLabel
							control={
								<Checkbox
									checked={formData.rememberMe}
									color="primary"
									onChange={handleInputChange('rememberMe')}
								/>
							}
							label="Ghi nhớ đăng nhập"
						/>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							disabled={loginMutation.isPending}
							sx={{ mt: 2 }}
						>
							{loginMutation.isPending ? 'Đang đăng nhập...' : 'Đăng nhập'}
						</Button>

						<Divider sx={{ my: 2 }}>
							<Typography variant="body2" color="text.secondary">
								HOẶC
							</Typography>
						</Divider>

						<Button
							fullWidth
							variant="outlined"
							startIcon={<GoogleIcon />}
							onClick={handleGoogleSignIn}
							sx={{
								textTransform: 'none',
								borderColor: 'divider',
								color: 'text.primary',
								'&:hover': {
									borderColor: 'primary.main',
									backgroundColor: 'action.hover'
								}
							}}
						>
							Đăng nhập bằng Google
						</Button>
					</Box>
				</Card>
			</SignInContainer>
		</>
	);
}